import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  cloneElement,
} from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { MdEdit, MdDelete, MdVisibility } from "react-icons/md";

/**
 * Props:
 * - placement: "bottom-start" | "bottom-end" | "right-start" | "right-end"
 * - offset: { x: number, y: number }
 * - zIndex: number
 */
export default function DropdownAcoes({
  lead,
  onEditar,
  onExcluir,
  onVisualizarLogs,
  children,
  placement = "right-start",
  offset = { x: 8, y: 0 },
  zIndex = 100000,
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 160 });

  const placeMenu = () => {
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();

    // posição base por placement
    let top = r.bottom;
    let left = r.left;
    let minWidth = Math.max(160, r.width);

    const p = placement;
    if (p.startsWith("bottom")) {
      top = r.bottom + offset.y;
      if (p.endsWith("start")) left = r.left + offset.x;
      else if (p.endsWith("end")) left = r.right - minWidth - offset.x;
    } else if (p.startsWith("right")) {
      top = p.endsWith("start") ? r.top + offset.y : r.bottom - 36 + offset.y; // 36 ~ altura média item
      left = r.right + offset.x;
    } else {
      // fallback
      top = r.bottom + offset.y;
      left = r.left + offset.x;
    }

    // clamp simples p/ não sair da tela
    const menuW = Math.max(minWidth, 200);
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // se estourar lateral direita, tenta flipar para esquerda/direita
    if (left + menuW > viewportW - 8) {
      if (p.startsWith("right")) {
        // abre para a esquerda do botão
        left = Math.max(8, r.left - menuW - offset.x);
      } else if (p.endsWith("end")) {
        left = Math.max(8, viewportW - menuW - 8);
      } else {
        left = Math.max(8, viewportW - menuW - 8);
      }
    }
    // se estourar topo ou base, ajusta
    const approxH = 120; // altura aproximada do menu
    if (top + approxH > viewportH - 8) {
      top = Math.max(8, viewportH - approxH - 8);
    }

    setPos({ top, left, minWidth: menuW });
  };

  useLayoutEffect(() => {
    if (open) placeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement, offset.x, offset.y]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onScroll = () => setOpen(false);
    const onResize = () => setOpen(false);

    document.addEventListener("mousedown", onDocClick, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize, true);

    return () => {
      document.removeEventListener("mousedown", onDocClick, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize, true);
    };
  }, [open]);

  const trigger = cloneElement(children, {
    ref: (node) => {
      // mescla ref externa, se houver
      const childRef = children.ref;
      if (typeof childRef === "function") childRef(node);
      else if (childRef && typeof childRef === "object")
        childRef.current = node;
      anchorRef.current = node;
    },
    onClick: (e) => {
      e.stopPropagation();
      children.props.onClick?.(e);
      setOpen((v) => !v);
    },
  });

  return (
    <>
      {trigger}
      {open &&
        createPortal(
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              minWidth: pos.minWidth,
              zIndex,
            }}
            className="bg-white border shadow-xl rounded-xl overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                onEditar?.(lead);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-blue-50"
            >
              <MdEdit className="w-5 h-5 text-blue-600" /> Editar
            </button>

            <button
              onClick={() => {
                onExcluir?.(lead?.id);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-red-50"
            >
              <MdDelete className="w-5 h-5 text-red-600" /> Excluir
            </button>

            <button
              onClick={() => {
                onVisualizarLogs?.(lead);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm hover:bg-slate-50"
            >
              <MdVisibility className="w-5 h-5 text-slate-600" /> Visualizar
            </button>
          </motion.div>,
          document.body
        )}
    </>
  );
}
